/*
 * -----------------------------------------------------------------------
 *  <copyright file="grid-filters.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { DropDownList } from '@progress/kendo-react-dropdowns';
import React, { useEffect, useRef } from 'react';

import DatePicker from './date-picker';
import { useTranslation } from '@hooks/index';

import { unixDateFromUtc } from '@utils/convertor';
import { NullableComponentRef, setAttribute } from '@utils/automation-test-helpers';

const buildTestId = (id: string) => `grid-filter-${id}`;

export const GridColumnMenuBooleanFilter = ({ firstFilterProps }: any) => {
    const { t } = useTranslation();

    const onChange = (event: any) => {
        firstFilterProps.onChange({
            value: event.target.value === 'true',
            operator: 'eq',
            syntheticEvent: event.syntheticEvent,
        });
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                    id="bool-true"
                    name="boolean"
                    type="radio"
                    value="true"
                    checked={firstFilterProps.value === true}
                    onChange={onChange}
                    data-testid={buildTestId('boolean-true')}
                />
                <label htmlFor="bool-true">&nbsp;{t('yes')}</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                    id="bool-false"
                    name="boolean"
                    type="radio"
                    value="false"
                    checked={firstFilterProps.value === false}
                    onChange={onChange}
                    data-testid={buildTestId('boolean-false')}
                />
                <label htmlFor="bool-false">&nbsp;{t('no')}</label>
            </div>
        </>
    );
};

export const GridColumnMenuActiveFilter = ({ firstFilterProps }: any) => {
    const { t } = useTranslation();
    const controls = [t('yes'), t('no')];
    const onChange = (event: any) => {
        firstFilterProps.onChange({
            value: event.target.value === t('yes'),
            operator: 'eq',
            syntheticEvent: event.syntheticEvent,
        });
    };

    const itemRender = (li: any) => {
        const children = <>{li.props.children}</>;
        const props = {
            ...li.props,
        };
        return React.cloneElement(li, props, children);
    };

    const ref = useRef<NullableComponentRef>(null);

    useEffect(() => setAttribute(ref, 'data-testid', buildTestId('active')));

    return (
        <DropDownList
            onChange={onChange}
            value={firstFilterProps.value === true ? t('yes') : firstFilterProps.value === false ? t('no') : undefined}
            itemRender={itemRender}
            defaultValue={t('selectState')}
            data={controls}
        />
    );
};

const filterDate = (selectedState: string, currentDate: any, date: any) => {
    if (selectedState === 'Is equal to') return currentDate === date;
    if (selectedState === 'Is after') return currentDate > date;
    if (selectedState === 'Is before') return currentDate < date;
    if (selectedState === 'Is not null') return currentDate;
    if (selectedState === '') return true;
};
export const GridColumnDateFilter = ({ firstFilterProps }: any) => {
    const values = ['Is equal to', 'Is after', 'Is before', 'Is not null', 'Is null'];
    const valuesBoolean = ['And', 'Or'];

    // note: formatDate needs to compare dates without time. so time just sets to 00:00:00

    const handleOperator = (current: any, next: any) => {
        const logicOperator = (next?.logic || valuesBoolean[0]) === 'And' ? '&&' : '||';
        // If filter is set to "is null" or "is not null" or vise-versa return all records
        const nullNotNullCheck =
            (next.dropdown === values[4] && next?.secondDropdown === values[3]) ||
            (next.dropdown === values[3] && next?.secondDropdown === values[4]);
        const firstDate = unixDateFromUtc(next?.firstDate);
        const secondDate = unixDateFromUtc(next?.secondDate);
        const currentValue = unixDateFromUtc(current, true);
        const first = filterDate(next?.dropdown || values[0], currentValue, firstDate);
        const second = filterDate(next?.secondDropdown || values[0], currentValue, secondDate);
        const selectedState = (next?.firstDate ? next?.dropdown : next?.secondDropdown) || values[0];
        let date = next?.firstDate ?? next?.secondDate;
        date = unixDateFromUtc(date);

        // Fist and Second dropdown Null/NotNull Check and clause is "OR" or "AND"
        if (nullNotNullCheck) {
            return logicOperator === '||';
        }
        // If First and Second dropdown is Null & Value
        if (
            (next.dropdown === values[4] && next?.secondDate) ||
            (next.secondDropdown === values[4] && next.firstDate)
        ) {
            return logicOperator === '&&' ? false : !unixDateFromUtc(current) || first || second;
        }
        // Final catch if only one value is null and the other values are not set
        if (next.dropdown === values[4] || next?.secondDropdown === values[4]) {
            return !unixDateFromUtc(current);
        }
        // first input or second has options is not null and date input doesn't have dates entered
        if (
            (next.dropdown === values[3] || next?.secondDropdown === values[3]) &&
            !next.firstDate &&
            !next.secondDate
        ) {
            return unixDateFromUtc(current);
        }
        // first input or second has options is not null and one of date inputs have dates entered
        if (
            (next.dropdown === values[3] || next?.secondDropdown === values[3]) &&
            (next?.firstDate || next?.secondDate)
        ) {
            if (logicOperator === '||') {
                return unixDateFromUtc(current);
            }
            return currentValue ? filterDate(selectedState, currentValue, date) : false;
        }

        // one of date inpus have date entered
        if ((next?.firstDate && !next?.secondDate) || (!next?.firstDate && next?.secondDate)) {
            return filterDate(selectedState, currentValue, date);
        }
        // If both values are populated
        if (next?.firstDate && next?.secondDate) {
            return logicOperator !== '||' ? first && second : first || second;
        }
    };

    const onChange = (key: string) => (event: any) => {
        firstFilterProps.onChange({
            value: {
                ...firstFilterProps.value,
                [key]: event.value,
            },
            operator: handleOperator,
            syntheticEvent: event.syntheticEvent,
        });
    };

    const itemRender = (li: any) => {
        const children = <>{li.props.children}</>;

        return React.cloneElement(li, li.props, children);
    };

    const firstOperator = useRef<NullableComponentRef>(null);
    const andOr = useRef<NullableComponentRef>(null);
    const secondOperator = useRef<NullableComponentRef>(null);

    useEffect(() => {
        setAttribute(firstOperator, 'data-testid', buildTestId('first-operator'));
        setAttribute(andOr, 'data-testid', buildTestId('and-or'));
        setAttribute(secondOperator, 'data-testid', buildTestId('second-operator'));
    });

    return (
        <>
            <DropDownList
                ref={firstOperator}
                onChange={(event: any) => {
                    firstFilterProps.onChange({
                        value: {
                            ...firstFilterProps.value,
                            dropdown: event.target.value,
                        },
                        operator: handleOperator,
                        syntheticEvent: event.syntheticEvent,
                    });
                }}
                value={firstFilterProps.value?.dropdown || values[0]}
                itemRender={itemRender}
                data={values}
            />
            <DatePicker
                handleDateChange={onChange('firstDate')}
                selectedDate={firstFilterProps.value?.firstDate}
                label=""
                customId="filter-first-date"
                data-testid={buildTestId('first-date')}
            />
            <DropDownList
                ref={andOr}
                onChange={(event: any) => {
                    firstFilterProps.onChange({
                        value: {
                            ...firstFilterProps.value,
                            logic: event.target.value,
                        },
                        operator: handleOperator,
                        syntheticEvent: event.syntheticEvent,
                    });
                }}
                value={firstFilterProps.value?.logic || valuesBoolean[0]}
                itemRender={itemRender}
                data={valuesBoolean}
            />
            <DropDownList
                ref={secondOperator}
                onChange={(event: any) => {
                    firstFilterProps.onChange({
                        value: {
                            ...firstFilterProps.value,
                            secondDropdown: event.target.value,
                        },
                        operator: handleOperator,
                        syntheticEvent: event.syntheticEvent,
                    });
                }}
                value={firstFilterProps.value?.secondDropdown || values[0]}
                itemRender={itemRender}
                data={values}
            />
            <DatePicker
                handleDateChange={onChange('secondDate')}
                selectedDate={firstFilterProps.value?.secondDate}
                label=""
                customId="filter-second-date"
                data-testid={buildTestId('second-date')}
            />
        </>
    );
};
